import {
  capitalizeFirstLetter,
  formatDate,
  formatFileSize,
  snakeCaseToCapitalized,
} from "@/utils/misc";
import { MetadataSection } from "./types";
import { AssetDto, AssetDtoStateEnum } from "generated";
import { ProducedByDto } from "@/hooks/types";

export const titleLookup = {
  IN_PROGRESS: null,
  VIEWED: "Marked as OK",
  DONE: "Uploaded",
  DENIED: "Denied",
};

export const metadataSections: MetadataSection[] = [
  {
    id: "negotiatedUsageRights",
    title: "Negotiated usage rights",
    transformation: (value, _producers, _producedBy, metadata) => {
      return metadata?.["Negotiated usage rights"]?.[value] ?? value;
    },
  },
  {
    id: "endOfNegotiatedUsageRights",
    title: "End of negotiated usage rights",
    transformation: (value) => formatDate(value) || "No expiration date",
  },
  {
    id: "deliveryName",
    title: "Campaign/Delivery name",
    transformation: (value, _producers, _producedBy, metadata) => {
      return metadata?.["Campaigns / Deliveries"]?.[value] ?? value;
    },
  },
  {
    id: "releaseDate",
    title: "Release date",
    transformation: formatDate,
  },
  { id: "channel", title: "Channel" },
  {
    id: "subDelivery",
    title: "Subdelivery",
    transformation: (value, _producers, _producedBy, metadata) => {
      return metadata?.["Sub delivery"]?.[value] ?? value;
    },
  },
  {
    id: "objective",
    title: "Objective",
    transformation: (value, _producers, _producedBy, metadata) => {
      return metadata?.["Objective"]?.[value] ?? value;
    },
  },
  { id: "fiscalYear", title: "Fiscal year" },
  {
    id: "contentType",
    title: "Content type",
    transformation: (
      value,
      _producers,
      _producedBy,
      _metadata,
      documentContentTypes,
    ) =>
      documentContentTypes?.[value] ?? snakeCaseToCapitalized(value) ?? value,
  },
  {
    id: "producedBy",
    title: "Produced by",
    transformation: (value, _, producedBy?) =>
      (producedBy ?? ({} as ProducedByDto))[value as keyof ProducedByDto],
  },
  { id: "launch", title: "Launch" },
  {
    id: "extension",
    title: "Extension",
  },
  {
    id: "producerId",
    title: "Team/Agency/Market",
    transformation: (value, producers?) => {
      const producer = producers?.filter(
        (producer) => producer.id === parseInt(value),
      )[0];

      return producer
        ? `[${capitalizeFirstLetter(producer.type)}] ${producer.name}`
        : value;
    },
  },
  {
    id: "filesize",
    title: "File size",
    transformation: (value) => formatFileSize(+value),
  },
  {
    id: "userDisclaimer",
    title: "User disclaimer",
    isOptionalField: true,
  },
  {
    id: "otherNegotiatedUsageRights",
    title: "Other usage rights",
    isOptionalField: true,
  },
  {
    id: "productionOrderedBy",
    title: "Production ordered by",
    isConditionallyRequired: (value, asset) =>
      !value && asset?.producedBy === "AGENCY",
  },
];

export const SELECT_ALL = "select-all";

export const AUTO_SELECTED_STATES: AssetDtoStateEnum[] = [
  "IN_PROGRESS",
  "VIEWED",
];

export const EXCLUDED_SECTIONS = new Set<keyof AssetDto>(["filesize"]);
